import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";
import { setUserContext } from "@/lib/monitoring";
import { syncUserTheme, applyDefaultThemeIfUnset, clearThemeOverride, applyTheme } from "@/lib/theme";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuperAdmin: boolean;
  isPremium: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({ user: null, session: null, loading: true, isSuperAdmin: false, isPremium: false, signOut: async () => {} });


async function applyProfileLanguage(userId: string) {
  const { data } = await supabase.from("profiles").select("language").eq("id", userId).maybeSingle();
  const profileLang = data?.language;
  if (profileLang && profileLang !== i18n.language) {
    await i18n.changeLanguage(profileLang);
    localStorage.setItem("lang", profileLang);
  }
}

async function fetchIsSuperAdmin(userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "super_admin").maybeSingle();
  return !!data;
}

async function fetchIsPremium(userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).in("role", ["premium", "super_admin"]);
  return !!data && data.length > 0;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    const sync = (s: Session | null) => {
      setSession(s);
      setLoading(false);
      if (s?.user) {
        setUserContext({ id: s.user.id, email: s.user.email ?? undefined });
        applyProfileLanguage(s.user.id);
        fetchIsSuperAdmin(s.user.id).then(setIsSuperAdmin);
        fetchIsPremium(s.user.id).then(setIsPremium);
        syncUserTheme(s.user.id);
      } else {
        setUserContext(null);
        setIsSuperAdmin(false);
        setIsPremium(false);
        clearThemeOverride();
        applyTheme("default");
        void applyDefaultThemeIfUnset();
      }
    };
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => sync(s));
    supabase.auth.getSession().then(({ data }) => sync(data.session));

    // When the tab/PWA comes back to the foreground after being idle, the
    // access token may have expired and the silent autoRefresh may have failed
    // (e.g. refresh token revoked). Without this check the UI still shows the
    // user as signed-in, but every write hits RLS / 401 ("JWT expired") and
    // actions like saving a tip silently fail until the user logs out & in.
    // We proactively re-validate the session on visibility/focus changes and
    // sign out cleanly if the refresh fails — that way the UI flips to the
    // login screen instead of being stuck in a broken state.
    let revalidating = false;
    const revalidate = async () => {
      if (revalidating) return;
      revalidating = true;
      try {
        const { data: { session: current } } = await supabase.auth.getSession();
        if (!current) return; // nothing to refresh; onAuthStateChange already handled it
        const expiresAt = (current.expires_at ?? 0) * 1000;
        // Refresh if token is expired or expires in the next 60 seconds.
        if (expiresAt - Date.now() < 60_000) {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            // Refresh token is gone/expired/revoked — sign out so UI updates.
            await supabase.auth.signOut().catch(() => {});
          }
        }
      } finally {
        revalidating = false;
      }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") void revalidate(); };
    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", revalidate);
    window.addEventListener("online", revalidate);

    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("online", revalidate);
    };
  }, []);

  return (
    <Ctx.Provider value={{
      user: session?.user ?? null,
      session,
      loading,
      isSuperAdmin,
      isPremium,
      signOut: async () => { await supabase.auth.signOut(); },
    }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);

